import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Shield, Users, CreditCard, Mail, LogOut, RefreshCw, DollarSign, Plus, Edit, Trash2, TrendingUp, AlertCircle, CheckCircle, Eye, Clock, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', name: '', subscription_plan: '', country: 'US', language: 'en' });
  
  // Missed reports state
  const [missedReports, setMissedReports] = useState([]);
  const [reportsStats, setReportsStats] = useState({ pending: 0, reviewed: 0 });
  
  // Waitlist state
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistStats, setWaitlistStats] = useState({ pending: 0, added: 0, notified: 0 });
  
  // Feature requests state
  const [featureRequests, setFeatureRequests] = useState([]);
  const [featureStats, setFeatureStats] = useState({ pending: 0, approved: 0, total: 0 });
  
  // Stripe settings state
  const [stripeSettings, setStripeSettings] = useState(null);
  const [isLoadingStripe, setIsLoadingStripe] = useState(false);
  const [isTogglingMode, setIsTogglingMode] = useState(false);
  const [stripeForm, setStripeForm] = useState({
    sandbox: { secret_key: '', webhook_secret: '', price_monthly: '', price_yearly: '', price_founding: '', price_quick_check: '' },
    live: { secret_key: '', webhook_secret: '', price_monthly: '', price_yearly: '', price_founding: '', price_quick_check: '' }
  });
  const [isSavingStripe, setIsSavingStripe] = useState(false);
  
  // App settings state
  const [appSettings, setAppSettings] = useState(null);
  const [isTogglingOtp, setIsTogglingOtp] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getUsers(0, 100)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      
      const token = localStorage.getItem('admin_token');
      
      // Load missed reports
      try {
        const reportsRes = await api.get('/admin/missed-reports', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMissedReports(reportsRes.data.reports || []);
        setReportsStats({
          pending: reportsRes.data.pending_count || 0,
          reviewed: reportsRes.data.reviewed_count || 0
        });
      } catch (e) {
        console.error('Failed to load reports:', e);
      }
      
      // Load waitlist
      try {
        const waitlistRes = await api.get('/admin/waitlist', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWaitlist(waitlistRes.data.entries || []);
        setWaitlistStats(waitlistRes.data.stats || { pending: 0, added: 0, notified: 0 });
      } catch (e) {
        console.error('Failed to load waitlist:', e);
      }
      
      // Load feature requests
      try {
        const featuresRes = await api.get('/admin/feature-requests', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const requests = featuresRes.data.requests || [];
        setFeatureRequests(requests);
        setFeatureStats({
          pending: requests.filter(r => !r.approved && r.status !== 'declined').length,
          approved: requests.filter(r => r.approved).length,
          total: requests.length
        });
      } catch (e) {
        console.error('Failed to load feature requests:', e);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handleCreateUser = async () => {
    try {
      await adminApi.createUser({
        email: newUser.email,
        name: newUser.name || null,
        subscription_plan: newUser.subscription_plan || null,
        country: newUser.country,
        language: newUser.language
      });
      toast.success('User created');
      setShowAddUser(false);
      setNewUser({ email: '', name: '', subscription_plan: '', country: 'US', language: 'en' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      await adminApi.updateUser(editingUser.id, {
        subscription_plan: editingUser.subscription_plan || null,
        name: editingUser.name || null,
        grant_quick_check: editingUser.grant_quick_check || null
      });
      toast.success('User updated');
      setEditingUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await adminApi.deleteUser(userId);
      toast.success('User deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleUpdateWaitlistStatus = async (entryId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await api.patch(`/admin/waitlist/${entryId}?status=${status}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Marked as ${status}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getPlanBadge = (user) => {
    if (user.subscription_plan) {
      const colors = {
        founding: 'bg-purple-100 text-purple-700',
        monthly: 'bg-blue-100 text-blue-700',
        yearly: 'bg-green-100 text-green-700',
      };
      return <Badge className={colors[user.subscription_plan] || 'bg-gray-100 text-gray-700'}>{user.subscription_plan}</Badge>;
    }
    if (user.has_quick_check) {
      return <Badge className="bg-orange-100 text-orange-700">Quick Check</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-700">Free</Badge>;
  };

  const handleUpdateReportStatus = async (reportId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await api.patch(`/admin/missed-reports/${reportId}?status=${status}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update report');
    }
  };

  // Feature request functions
  const handleApproveFeature = async (requestId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await api.patch(`/admin/feature-requests/${requestId}?approved=true`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feature request approved and visible to users');
      loadData();
    } catch (error) {
      toast.error('Failed to approve feature request');
    }
  };

  const handleUpdateFeatureStatus = async (requestId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await api.patch(`/admin/feature-requests/${requestId}?status=${status}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feature request status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update feature request');
    }
  };

  const handleDeleteFeature = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this feature request?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await api.delete(`/admin/feature-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feature request deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete feature request');
    }
  };

  // Stripe settings functions
  const loadStripeSettings = async () => {
    setIsLoadingStripe(true);
    try {
      const response = await adminApi.getStripeSettings();
      setStripeSettings(response.data);
      // Pre-fill form with price IDs (not keys - they're masked)
      setStripeForm({
        sandbox: {
          secret_key: '',
          webhook_secret: '',
          price_monthly: response.data.sandbox?.price_monthly || '',
          price_yearly: response.data.sandbox?.price_yearly || '',
          price_founding: response.data.sandbox?.price_founding || '',
          price_quick_check: response.data.sandbox?.price_quick_check || ''
        },
        live: {
          secret_key: '',
          webhook_secret: '',
          price_monthly: response.data.live?.price_monthly || '',
          price_yearly: response.data.live?.price_yearly || '',
          price_founding: response.data.live?.price_founding || '',
          price_quick_check: response.data.live?.price_quick_check || ''
        }
      });
    } catch (error) {
      toast.error('Failed to load Stripe settings');
    } finally {
      setIsLoadingStripe(false);
    }
  };

  // App settings functions
  const loadAppSettings = async () => {
    try {
      const response = await adminApi.getAppSettings();
      setAppSettings(response.data);
    } catch (error) {
      console.error('Failed to load app settings');
    }
  };

  const handleToggleOtpDisplay = async () => {
    setIsTogglingOtp(true);
    try {
      const response = await adminApi.toggleOtpDisplay();
      setAppSettings(prev => ({ ...prev, show_otp_on_screen: response.data.show_otp_on_screen }));
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to toggle OTP display');
    } finally {
      setIsTogglingOtp(false);
    }
  };

  const handleToggleStripeMode = async () => {
    setIsTogglingMode(true);
    try {
      const response = await adminApi.toggleStripeMode();
      toast.success(`Switched to ${response.data.mode} mode`);
      loadStripeSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle mode');
    } finally {
      setIsTogglingMode(false);
    }
  };

  const handleSaveStripeSettings = async (mode) => {
    setIsSavingStripe(true);
    try {
      const data = {};
      data[mode] = {};
      
      // Only send non-empty values
      if (stripeForm[mode].secret_key) data[mode].secret_key = stripeForm[mode].secret_key;
      if (stripeForm[mode].webhook_secret) data[mode].webhook_secret = stripeForm[mode].webhook_secret;
      if (stripeForm[mode].price_monthly) data[mode].price_monthly = stripeForm[mode].price_monthly;
      if (stripeForm[mode].price_yearly) data[mode].price_yearly = stripeForm[mode].price_yearly;
      if (stripeForm[mode].price_founding) data[mode].price_founding = stripeForm[mode].price_founding;
      if (stripeForm[mode].price_quick_check) data[mode].price_quick_check = stripeForm[mode].price_quick_check;
      
      await adminApi.updateStripeSettings(data);
      toast.success(`${mode.charAt(0).toUpperCase() + mode.slice(1)} settings saved`);
      // Clear sensitive fields after save
      setStripeForm(prev => ({
        ...prev,
        [mode]: { ...prev[mode], secret_key: '', webhook_secret: '' }
      }));
      loadStripeSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSavingStripe(false);
    }
  };

  // Load Stripe settings when tab is selected
  useEffect(() => {
    if (activeTab === 'stripe' && !stripeSettings) {
      loadStripeSettings();
      loadAppSettings();
    }
  }, [activeTab]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6ee7b7]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#6ee7b7]" />
            <span className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>SubGuard Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={loadData} className="text-gray-400 hover:text-white">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-gray-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />Logout
            </Button>
          </div>
        </div>
        {/* Tabs */}
        <div className="flex gap-4 mt-4 flex-wrap">
          {['overview', 'users', 'waitlist', 'reports', 'features', 'revenue', 'stripe'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                activeTab === tab ? 'bg-[#6ee7b7] text-gray-900' : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab === 'reports' && reportsStats.pending > 0 && (
                <span className="w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                  {reportsStats.pending}
                </span>
              )}
              {tab === 'waitlist' && waitlistStats.pending > 0 && (
                <span className="w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                  {waitlistStats.pending}
                </span>
              )}
              {tab === 'features' && featureStats.pending > 0 && (
                <span className="w-5 h-5 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center">
                  {featureStats.pending}
                </span>
              )}
              {tab === 'stripe' && (
                <CreditCard className="w-4 h-4" />
              )}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </header>

      <div className="p-6">
        {activeTab === 'overview' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Total Users</p>
                      <p className="text-2xl font-bold text-white">{stats?.total_users || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Total Revenue</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(stats?.revenue?.total_one_time)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Monthly Recurring</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(stats?.revenue?.total_recurring_monthly)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Gmail Connections</p>
                      <p className="text-2xl font-bold text-white">{stats?.gmail_connections || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Users by Plan */}
            <Card className="bg-gray-800 border-gray-700 mb-8">
              <CardHeader><CardTitle className="text-white">Users by Plan</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  {stats?.users_by_plan && Object.entries(stats.users_by_plan).map(([plan, count]) => (
                    <div key={plan} className="bg-gray-700/50 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-white">{count}</p>
                      <p className="text-gray-400 text-sm capitalize">{plan}</p>
                    </div>
                  ))}
                  <div className="bg-orange-500/20 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-orange-400">{stats?.active_quick_checks || 0}</p>
                    <p className="text-gray-400 text-sm">Quick Checks</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Subscription Metrics */}
            {stats?.subscription_metrics && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                          <CreditCard className="w-6 h-6 text-cyan-400" />
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Total Tracked</p>
                          <p className="text-2xl font-bold text-white">{stats.subscription_metrics.total_tracked}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Active Subs</p>
                          <p className="text-2xl font-bold text-white">{stats.subscription_metrics.active_subscriptions}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                          <DollarSign className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Monthly Value</p>
                          <p className="text-2xl font-bold text-white">{formatCurrency(stats.subscription_metrics.monthly_value_tracked)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-rose-500/20 rounded-xl flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-rose-400" />
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">Yearly Value</p>
                          <p className="text-2xl font-bold text-white">{formatCurrency(stats.subscription_metrics.yearly_value_tracked)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  {/* Top Subscriptions */}
                  <Card className="bg-gray-800 border-gray-700">
                    <CardHeader><CardTitle className="text-white">Top Subscriptions</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {stats.subscription_metrics.top_subscriptions?.slice(0, 8).map((sub, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-gray-700/30 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              <span className="text-gray-500 text-sm w-6">#{idx + 1}</span>
                              <span className="text-white font-medium">{sub.name}</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-gray-400 text-sm">{sub.active} active</span>
                              <Badge className="bg-cyan-500/20 text-cyan-400 border-0">{sub.count} total</Badge>
                            </div>
                          </div>
                        ))}
                        {(!stats.subscription_metrics.top_subscriptions || stats.subscription_metrics.top_subscriptions.length === 0) && (
                          <p className="text-gray-500 text-center py-4">No subscriptions tracked yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* By Category */}
                  <Card className="bg-gray-800 border-gray-700">
                    <CardHeader><CardTitle className="text-white">By Category</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {stats.subscription_metrics.by_category?.map((cat, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-gray-700/30 rounded-lg p-3">
                            <span className="text-white font-medium capitalize">{cat.category}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-emerald-400 font-medium">{formatCurrency(cat.total_amount)}</span>
                              <Badge className="bg-gray-600 text-gray-300 border-0">{cat.count}</Badge>
                            </div>
                          </div>
                        ))}
                        {(!stats.subscription_metrics.by_category || stats.subscription_metrics.by_category.length === 0) && (
                          <p className="text-gray-500 text-center py-4">No categories yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'users' && (
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Users</CardTitle>
              <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
                <DialogTrigger asChild>
                  <Button className="bg-[#6ee7b7] text-gray-900 hover:bg-[#5dd3a3]">
                    <Plus className="w-4 h-4 mr-2" />Add User
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-gray-800 border-gray-700">
                  <DialogHeader><DialogTitle className="text-white">Add New User</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-300">Email *</Label>
                      <Input value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} className="bg-gray-700 border-gray-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-gray-300">Name</Label>
                      <Input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className="bg-gray-700 border-gray-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-gray-300">Plan</Label>
                      <Select value={newUser.subscription_plan} onValueChange={v => setNewUser({...newUser, subscription_plan: v === 'free' ? '' : v})}>
                        <SelectTrigger className="bg-gray-700 border-gray-600 text-white"><SelectValue placeholder="Select plan" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="founding">Founding Guard ($49)</SelectItem>
                          <SelectItem value="monthly">Monthly ($2.99)</SelectItem>
                          <SelectItem value="yearly">Yearly ($29)</SelectItem>
                          <SelectItem value="quick_check">Quick Check ($4.99)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleCreateUser} className="w-full bg-[#6ee7b7] text-gray-900">Create User</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700">
                    <TableHead className="text-gray-400">Email</TableHead>
                    <TableHead className="text-gray-400">Name</TableHead>
                    <TableHead className="text-gray-400">Plan</TableHead>
                    <TableHead className="text-gray-400">Joined</TableHead>
                    <TableHead className="text-gray-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} className="border-gray-700">
                      <TableCell className="text-white">{user.email}</TableCell>
                      <TableCell className="text-gray-300">{user.name || '-'}</TableCell>
                      <TableCell>{getPlanBadge(user)}</TableCell>
                      <TableCell className="text-gray-400 text-sm">{formatDate(user.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Dialog open={editingUser?.id === user.id} onOpenChange={(open) => !open && setEditingUser(null)}>
                            <DialogTrigger asChild>
                              <Button size="sm" variant="ghost" onClick={() => setEditingUser({...user})} className="text-gray-400 hover:text-white">
                                <Edit className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-gray-800 border-gray-700">
                              <DialogHeader><DialogTitle className="text-white">Edit User</DialogTitle></DialogHeader>
                              {editingUser && (
                                <div className="space-y-4">
                                  <div>
                                    <Label className="text-gray-300">Email</Label>
                                    <Input value={editingUser.email} disabled className="bg-gray-700 border-gray-600 text-gray-400" />
                                  </div>
                                  <div>
                                    <Label className="text-gray-300">Name</Label>
                                    <Input value={editingUser.name || ''} onChange={e => setEditingUser({...editingUser, name: e.target.value})} className="bg-gray-700 border-gray-600 text-white" />
                                  </div>
                                  <div>
                                    <Label className="text-gray-300">Plan</Label>
                                    <Select value={editingUser.subscription_plan || 'free'} onValueChange={v => setEditingUser({...editingUser, subscription_plan: v === 'free' ? null : v})}>
                                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white"><SelectValue placeholder="Select plan" /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="free">Free</SelectItem>
                                        <SelectItem value="founding">Founding Guard</SelectItem>
                                        <SelectItem value="monthly">Monthly</SelectItem>
                                        <SelectItem value="yearly">Yearly</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  {!editingUser.has_quick_check && !editingUser.subscription_plan && (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        id="grantQuickCheck"
                                        checked={editingUser.grant_quick_check || false}
                                        onChange={(e) => setEditingUser({...editingUser, grant_quick_check: e.target.checked})}
                                        className="w-4 h-4 rounded border-gray-600 text-orange-500"
                                      />
                                      <label htmlFor="grantQuickCheck" className="text-sm text-orange-400">
                                        Grant Quick Check (30-day scan access)
                                      </label>
                                    </div>
                                  )}
                                  {editingUser.has_quick_check && (
                                    <div className="p-3 bg-orange-900/30 border border-orange-700 rounded-lg">
                                      <p className="text-sm text-orange-300">Has active Quick Check</p>
                                      {editingUser.quick_check_expires && (
                                        <p className="text-xs text-orange-400">Expires: {new Date(editingUser.quick_check_expires).toLocaleDateString()}</p>
                                      )}
                                    </div>
                                  )}
                                  <Button onClick={handleUpdateUser} className="w-full bg-[#6ee7b7] text-gray-900">Save Changes</Button>
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteUser(user.id, user.email)} className="text-red-400 hover:text-red-300">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {activeTab === 'reports' && (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Missed Subscription Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 mb-6">
                  <div className="px-4 py-2 bg-orange-500/20 rounded-lg">
                    <span className="text-orange-400 text-sm">Pending: {reportsStats.pending}</span>
                  </div>
                  <div className="px-4 py-2 bg-green-500/20 rounded-lg">
                    <span className="text-green-400 text-sm">Reviewed: {reportsStats.reviewed}</span>
                  </div>
                </div>
                
                {missedReports.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-700">
                        <TableHead className="text-gray-400">Service</TableHead>
                        <TableHead className="text-gray-400">Amount</TableHead>
                        <TableHead className="text-gray-400">User</TableHead>
                        <TableHead className="text-gray-400">Notes</TableHead>
                        <TableHead className="text-gray-400">Status</TableHead>
                        <TableHead className="text-gray-400">Date</TableHead>
                        <TableHead className="text-gray-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {missedReports.map((report) => (
                        <TableRow key={report.id} className="border-gray-700">
                          <TableCell className="text-white font-medium">{report.service_name}</TableCell>
                          <TableCell className="text-gray-300">
                            {report.estimated_amount ? `$${report.estimated_amount}` : '-'}
                          </TableCell>
                          <TableCell className="text-gray-300 text-sm">{report.user_email}</TableCell>
                          <TableCell className="text-gray-400 text-sm max-w-xs truncate">
                            {report.notes || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className={report.status === 'pending' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}>
                              {report.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-400 text-sm">{formatDate(report.created_at)}</TableCell>
                          <TableCell>
                            {report.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleUpdateReportStatus(report.id, 'reviewed')}
                                className="text-green-400 hover:text-green-300"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Mark Reviewed
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No missed subscription reports yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'waitlist' && (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5" />
                    Early Access Waitlist
                  </div>
                  {waitlist.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={async () => {
                        if (window.confirm(`Are you sure you want to clear all ${waitlist.length} waitlist entries? This cannot be undone.`)) {
                          try {
                            await adminApi.clearWaitlist();
                            setWaitlist([]);
                            setWaitlistStats({ pending: 0, added: 0, notified: 0 });
                            toast.success('Waitlist cleared successfully');
                          } catch (e) {
                            toast.error('Failed to clear waitlist');
                          }
                        }
                      }}
                      data-testid="clear-waitlist-btn"
                    >
                      Clear All
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 mb-6">
                  <div className="px-4 py-2 bg-blue-500/20 rounded-lg">
                    <span className="text-blue-400 text-sm">Pending: {waitlistStats.pending}</span>
                  </div>
                  <div className="px-4 py-2 bg-green-500/20 rounded-lg">
                    <span className="text-green-400 text-sm">Added: {waitlistStats.added}</span>
                  </div>
                  <div className="px-4 py-2 bg-purple-500/20 rounded-lg">
                    <span className="text-purple-400 text-sm">Notified: {waitlistStats.notified}</span>
                  </div>
                </div>
                
                {waitlist.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-700">
                        <TableHead className="text-gray-400">#</TableHead>
                        <TableHead className="text-gray-400">Email</TableHead>
                        <TableHead className="text-gray-400">Status</TableHead>
                        <TableHead className="text-gray-400">Joined</TableHead>
                        <TableHead className="text-gray-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {waitlist.map((entry, index) => (
                        <TableRow key={entry.id} className="border-gray-700">
                          <TableCell className="text-gray-400">{entry.position || index + 1}</TableCell>
                          <TableCell className="text-white font-medium">{entry.email}</TableCell>
                          <TableCell>
                            <Badge className={
                              entry.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                              entry.status === 'added' ? 'bg-green-100 text-green-700' :
                              entry.status === 'notified' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {entry.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-gray-400 text-sm">{formatDate(entry.joined_at)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {entry.status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleUpdateWaitlistStatus(entry.id, 'added')}
                                    className="text-green-400 hover:text-green-300"
                                    title="Mark as added to test users"
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Added
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleUpdateWaitlistStatus(entry.id, 'notified')}
                                    className="text-purple-400 hover:text-purple-300"
                                    title="Mark as notified"
                                  >
                                    <Mail className="w-4 h-4 mr-1" />
                                    Notified
                                  </Button>
                                </>
                              )}
                              {entry.status === 'added' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleUpdateWaitlistStatus(entry.id, 'notified')}
                                  className="text-purple-400 hover:text-purple-300"
                                >
                                  <Mail className="w-4 h-4 mr-1" />
                                  Notified
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12">
                    <UserPlus className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No waitlist signups yet</p>
                    <p className="text-gray-500 text-sm mt-2">Share your early access page to start collecting signups</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'features' && (
          <div className="space-y-6">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Feature Requests
                  </div>
                  <div className="flex gap-2">
                    <Badge className="bg-purple-500/20 text-purple-400 border-0">
                      {featureStats.pending} pending
                    </Badge>
                    <Badge className="bg-green-500/20 text-green-400 border-0">
                      {featureStats.approved} approved
                    </Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {featureRequests.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-700">
                        <TableHead className="text-gray-400">Title</TableHead>
                        <TableHead className="text-gray-400">From</TableHead>
                        <TableHead className="text-gray-400">Category</TableHead>
                        <TableHead className="text-gray-400">Votes</TableHead>
                        <TableHead className="text-gray-400">Status</TableHead>
                        <TableHead className="text-gray-400">Visibility</TableHead>
                        <TableHead className="text-gray-400">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {featureRequests.map((req) => (
                        <TableRow key={req.id} className="border-gray-700">
                          <TableCell>
                            <div>
                              <p className="text-white font-medium">{req.title}</p>
                              <p className="text-gray-500 text-xs mt-1 max-w-xs truncate">{req.description}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-300 text-sm">{req.user_email}</TableCell>
                          <TableCell>
                            <Badge className="bg-gray-700 text-gray-300 capitalize">{req.category || 'general'}</Badge>
                          </TableCell>
                          <TableCell className="text-white font-semibold">{req.votes}</TableCell>
                          <TableCell>
                            <Select 
                              value={req.status} 
                              onValueChange={(value) => handleUpdateFeatureStatus(req.id, value)}
                            >
                              <SelectTrigger className="w-32 bg-gray-700 border-gray-600 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-gray-700">
                                <SelectItem value="pending" className="text-gray-300">Pending</SelectItem>
                                <SelectItem value="approved" className="text-gray-300">Approved</SelectItem>
                                <SelectItem value="planned" className="text-gray-300">Planned</SelectItem>
                                <SelectItem value="in_progress" className="text-gray-300">In Progress</SelectItem>
                                <SelectItem value="completed" className="text-gray-300">Completed</SelectItem>
                                <SelectItem value="declined" className="text-gray-300">Declined</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {req.approved ? (
                              <Badge className="bg-green-500/20 text-green-400 border-0">Visible</Badge>
                            ) : (
                              <Badge className="bg-yellow-500/20 text-yellow-400 border-0">Hidden</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {!req.approved && (
                                <Button
                                  size="sm"
                                  onClick={() => handleApproveFeature(req.id)}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteFeature(req.id)}
                                className="border-red-500 text-red-400 hover:bg-red-500/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12">
                    <Eye className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No feature requests yet</p>
                    <p className="text-gray-500 text-sm mt-2">Feature requests will appear here for approval</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'revenue' && (
          <div className="space-y-6">
            {/* Revenue Breakdown */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader><CardTitle className="text-white">Revenue Breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                    <p className="text-purple-300 text-sm">Founding Guard</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(stats?.revenue?.founding)}</p>
                    <p className="text-purple-400 text-xs">{stats?.users_by_plan?.founding || 0} users × $49</p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                    <p className="text-blue-300 text-sm">Monthly MRR</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(stats?.revenue?.monthly_mrr)}</p>
                    <p className="text-blue-400 text-xs">{stats?.users_by_plan?.monthly || 0} users × $2.99/mo</p>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <p className="text-green-300 text-sm">Yearly ARR</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(stats?.revenue?.yearly_arr)}</p>
                    <p className="text-green-400 text-xs">{stats?.users_by_plan?.yearly || 0} users × $29/yr</p>
                  </div>
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                    <p className="text-orange-300 text-sm">Quick Check</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(stats?.revenue?.quick_check)}</p>
                    <p className="text-orange-400 text-xs">{stats?.active_quick_checks || 0} purchases × $4.99</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader><CardTitle className="text-white">Summary</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-gray-700/30 rounded-xl">
                    <p className="text-gray-400 text-sm mb-2">Total One-Time Revenue</p>
                    <p className="text-3xl font-bold text-[#6ee7b7]">{formatCurrency(stats?.revenue?.total_one_time)}</p>
                  </div>
                  <div className="text-center p-6 bg-gray-700/30 rounded-xl">
                    <p className="text-gray-400 text-sm mb-2">Monthly Recurring Revenue</p>
                    <p className="text-3xl font-bold text-[#6ee7b7]">{formatCurrency(stats?.revenue?.total_recurring_monthly)}</p>
                  </div>
                  <div className="text-center p-6 bg-gray-700/30 rounded-xl">
                    <p className="text-gray-400 text-sm mb-2">Annualized Revenue</p>
                    <p className="text-3xl font-bold text-[#6ee7b7]">{formatCurrency((stats?.revenue?.total_one_time || 0) + (stats?.revenue?.total_recurring_monthly || 0) * 12)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'stripe' && (
          <div className="space-y-6">
            {/* OTP Display Toggle */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    OTP Display Settings
                  </div>
                  {appSettings && (
                    <Badge className={appSettings.show_otp_on_screen ? 'bg-yellow-500' : 'bg-green-500'}>
                      {appSettings.show_otp_on_screen ? 'DEV MODE' : 'PRODUCTION'}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-xl">
                  <div>
                    <p className="text-white font-medium">
                      Show OTP on Screen: {appSettings?.show_otp_on_screen ? 'Enabled' : 'Disabled'}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {appSettings?.show_otp_on_screen 
                        ? 'OTP is displayed on login screen (for testing)' 
                        : 'OTP only sent via email (production mode)'}
                    </p>
                  </div>
                  <Button
                    onClick={handleToggleOtpDisplay}
                    disabled={isTogglingOtp}
                    className={appSettings?.show_otp_on_screen 
                      ? 'bg-green-500 hover:bg-green-600' 
                      : 'bg-yellow-500 hover:bg-yellow-600'}
                  >
                    {isTogglingOtp ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    {appSettings?.show_otp_on_screen ? 'Disable (Go Live)' : 'Enable (Dev Mode)'}
                  </Button>
                </div>
                {!appSettings?.show_otp_on_screen && (
                  <div className="flex items-center gap-2 p-3 mt-4 bg-green-900/30 border border-green-700 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 text-sm">Production mode - OTP only sent via email</span>
                  </div>
                )}
                {appSettings?.show_otp_on_screen && (
                  <div className="flex items-center gap-2 p-3 mt-4 bg-yellow-900/30 border border-yellow-700 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-yellow-400" />
                    <span className="text-yellow-400 text-sm">Dev mode active - OTP visible on screen (disable before go-live)</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Mode Toggle Card */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Payment Mode
                  </div>
                  {stripeSettings && (
                    <Badge className={stripeSettings.mode === 'live' ? 'bg-green-500' : 'bg-yellow-500'}>
                      {stripeSettings.mode === 'live' ? 'LIVE' : 'SANDBOX'}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingStripe ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : stripeSettings ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-700 rounded-xl">
                      <div>
                        <p className="text-white font-medium">Current Mode: {stripeSettings.mode === 'live' ? 'Live' : 'Sandbox'}</p>
                        <p className="text-gray-400 text-sm">
                          {stripeSettings.mode === 'live' 
                            ? 'Processing real payments' 
                            : 'Testing mode - no real charges'}
                        </p>
                      </div>
                      <Button
                        onClick={handleToggleStripeMode}
                        disabled={isTogglingMode}
                        className={stripeSettings.mode === 'live' 
                          ? 'bg-yellow-500 hover:bg-yellow-600' 
                          : 'bg-green-500 hover:bg-green-600'}
                      >
                        {isTogglingMode ? (
                          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Switch to {stripeSettings.mode === 'live' ? 'Sandbox' : 'Live'}
                      </Button>
                    </div>
                    
                    {stripeSettings.mode === 'live' && (
                      <div className="flex items-center gap-2 p-3 bg-green-900/30 border border-green-700 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="text-green-400 text-sm">Live mode is active - real payments are being processed</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400">Failed to load settings</p>
                )}}
              </CardContent>
            </Card>

            {/* Sandbox Settings */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-400" />
                  Sandbox Settings (Test Mode)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Secret Key</Label>
                    <Input
                      type="password"
                      placeholder={stripeSettings?.sandbox?.has_secret_key ? '••••••••' : 'sk_test_...'}
                      value={stripeForm.sandbox.secret_key}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        sandbox: { ...prev.sandbox, secret_key: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                    {stripeSettings?.sandbox?.has_secret_key && (
                      <p className="text-xs text-gray-500 mt-1">Current: {stripeSettings.sandbox.secret_key_masked}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-400">Webhook Secret</Label>
                    <Input
                      type="password"
                      placeholder={stripeSettings?.sandbox?.has_webhook_secret ? '••••••••' : 'whsec_...'}
                      value={stripeForm.sandbox.webhook_secret}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        sandbox: { ...prev.sandbox, webhook_secret: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Monthly Price ID</Label>
                    <Input
                      placeholder="price_..."
                      value={stripeForm.sandbox.price_monthly}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        sandbox: { ...prev.sandbox, price_monthly: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Yearly Price ID</Label>
                    <Input
                      placeholder="price_..."
                      value={stripeForm.sandbox.price_yearly}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        sandbox: { ...prev.sandbox, price_yearly: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Founding Price ID</Label>
                    <Input
                      placeholder="price_..."
                      value={stripeForm.sandbox.price_founding}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        sandbox: { ...prev.sandbox, price_founding: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Quick Check Price ID</Label>
                    <Input
                      placeholder="price_..."
                      value={stripeForm.sandbox.price_quick_check}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        sandbox: { ...prev.sandbox, price_quick_check: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <Button 
                  onClick={() => handleSaveStripeSettings('sandbox')}
                  disabled={isSavingStripe}
                  className="bg-yellow-500 hover:bg-yellow-600"
                >
                  {isSavingStripe ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Sandbox Settings
                </Button>
              </CardContent>
            </Card>

            {/* Live Settings */}
            <Card className="bg-gray-800 border-gray-700 border-green-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Live Settings (Production)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Secret Key</Label>
                    <Input
                      type="password"
                      placeholder={stripeSettings?.live?.has_secret_key ? '••••••••' : 'sk_live_...'}
                      value={stripeForm.live.secret_key}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        live: { ...prev.live, secret_key: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                    {stripeSettings?.live?.has_secret_key && (
                      <p className="text-xs text-gray-500 mt-1">Current: {stripeSettings.live.secret_key_masked}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-400">Webhook Secret</Label>
                    <Input
                      type="password"
                      placeholder={stripeSettings?.live?.has_webhook_secret ? '••••••••' : 'whsec_...'}
                      value={stripeForm.live.webhook_secret}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        live: { ...prev.live, webhook_secret: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Monthly Price ID</Label>
                    <Input
                      placeholder="price_..."
                      value={stripeForm.live.price_monthly}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        live: { ...prev.live, price_monthly: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Yearly Price ID</Label>
                    <Input
                      placeholder="price_..."
                      value={stripeForm.live.price_yearly}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        live: { ...prev.live, price_yearly: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Founding Price ID</Label>
                    <Input
                      placeholder="price_..."
                      value={stripeForm.live.price_founding}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        live: { ...prev.live, price_founding: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Quick Check Price ID</Label>
                    <Input
                      placeholder="price_..."
                      value={stripeForm.live.price_quick_check}
                      onChange={(e) => setStripeForm(prev => ({
                        ...prev,
                        live: { ...prev.live, price_quick_check: e.target.value }
                      }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <Button 
                  onClick={() => handleSaveStripeSettings('live')}
                  disabled={isSavingStripe}
                  className="bg-green-500 hover:bg-green-600"
                >
                  {isSavingStripe ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Live Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
